import { useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { formSchemasApi } from '@/api/config-governance';
import { FormSchemaEditor } from '@/components/formSchemas/FormSchemaEditor';
import type { ChangeEvent, FormSchema } from '@/types/config-governance';
import styles from './platform-pages.module.css';

/**
 * ONE form schema version in the platform console: the section/field builder,
 * a live preview and the lifecycle. All of it lives in the shared
 * FormSchemaEditor — this page is the thin platform shell: it fetches the
 * version, fetches its history, and hands the editor the plain platform API
 * and the platform console's URLs. The institution console has its own thin
 * shell over the same editor (AdminFormSchemaDetailPage).
 */

export function FormSchemaDetailPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { schemaId = '' } = useParams();

  const fetcher = useCallback(() => formSchemasApi.get(schemaId), [schemaId]);
  const { state, reload, replace } = useAsyncData<FormSchema>(`form-schema:${schemaId}`, fetcher);

  const historyFetcher = useCallback(() => formSchemasApi.history(schemaId), [schemaId]);
  const history = useAsyncData<ChangeEvent[]>(`form-schema-history:${schemaId}`, historyFetcher);

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('studio.breadcrumbLabel')}>
        <Link to="/admin/form-schemas">{t('formSchemas.title')}</Link>
        <Icon name="chevronRight" size={14} aria-hidden="true" />
        <span>{state.status === 'ready' ? state.data.code : '…'}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(schema) => (
          // Keyed on the id only — a remount on updatedAt would wipe the
          // server report a save just fetched; sections and fields resync
          // through the effect instead.
          <FormSchemaEditor
            key={schema.id}
            schema={schema}
            replace={(next) => {
              replace(next);
              history.reload();
            }}
            historyState={history}
            onDeleted={() => navigate('/admin/form-schemas')}
            toast={toast}
            api={formSchemasApi}
            detailFor={(id) => `/admin/form-schemas/${encodeURIComponent(id)}`}
            baseHref={(id) => `/admin/form-schemas/${encodeURIComponent(id)}`}
          />
        )}
      </AsyncPanel>
    </div>
  );
}